import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Send, ThumbsUp, ThumbsDown, Check, ExternalLink } from "lucide-react";
import { stripDocCodes } from "@/lib/stripDocCodes";


type Ground = "settled_law" | "documented_position" | "live_conversation" | null;
type Cta = "none" | "book_call" | "access_fddr";

interface AssistantMsg {
  role: "assistant";
  content: string;
  ground?: Ground;
  section_title?: string | null;
  cta?: Cta;
  cta_url?: string | null;
  failed?: boolean;
  retryText?: string;
}
interface UserMsg {
  role: "user";
  content: string;
}
type Msg = AssistantMsg | UserMsg;

const FN_BASE = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;
const ANON = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

// Scoped design tokens — this page only.
const PAPER = "#FAF7F0";
const INK = "#1F3D2B";
const GOLD = "#B08A3C";
const GOLD_DARK = "#8F6E2E";
const HAIRLINE = "#D8D2C4";
const MUTED = "#6B7A6E";

const SERIF = "Georgia, 'Times New Roman', serif";
const SANS =
  "system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

const GROUND_LABEL: Record<Exclude<Ground, null>, string> = {
  settled_law: "Settled law",
  documented_position: "Our documented position",
  live_conversation: "Belongs in a live conversation",
};

const CTA_LABEL: Record<Exclude<Cta, "none">, string> = {
  book_call: "Book your call",
  access_fddr: "Request access to the full report",
};

// Prospect-facing openers: the skeptical questions, which retrieve best.
const STARTER_CHIPS = [
  "Is this actually legal?",
  "Do I have to start a church?",
  "What about taxes?",
];

const INTRO_TAIL =
  "Ask me anything about what you're reading here, including the skeptical questions. Those are usually the best ones. I'll keep every answer plain, and I'll point you to the part of the briefing it comes from.";

function buildIntro(firstName: string | null): AssistantMsg {
  const greeting = firstName ? `Hi ${firstName}, I'm Stew.` : "Hi, I'm Stew.";
  return {
    role: "assistant",
    content: `${greeting} ${INTRO_TAIL}`,
    cta: "none",
  };
}


const DISCLOSURE =
  "Stew is an AI assistant. It teaches, it does not advise. Conversations may be reviewed by our team to prepare for your call.";

async function post(path: string, body: unknown, init?: RequestInit) {
  return fetch(`${FN_BASE}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: ANON,
      Authorization: `Bearer ${ANON}`,
    },
    body: JSON.stringify(body),
    ...init,
  });
}

type Gate =
  | { kind: "loading" }
  | { kind: "missing" }
  | { kind: "forbidden" }
  | { kind: "unavailable" }
  | { kind: "ready"; token: string };

// Scoped CSS: only applies inside .stew-widget-root.
const SCOPED_CSS = `
.stew-widget-root { background: ${PAPER}; color: ${INK}; font-family: ${SANS}; }
.stew-widget-root .sw-prose { font-family: ${SERIF}; font-size: 17px; line-height: 1.7; color: ${INK}; white-space: pre-wrap; }
.stew-widget-root .sw-user-bubble { background: ${INK}; color: ${PAPER}; font-family: ${SANS}; font-size: 15px; line-height: 1.4; border-radius: 14px; padding: 8px 12px; max-width: 85%; }
.stew-widget-root .sw-citation-rule { width: 24px; height: 2px; background: ${GOLD}; margin-top: 10px; }
.stew-widget-root .sw-citation-text { text-transform: uppercase; letter-spacing: 0.08em; font-size: 11px; color: ${MUTED}; font-family: ${SANS}; margin-top: 6px; }
.stew-widget-root .sw-cta { background: ${GOLD}; color: ${PAPER}; border: 1px solid ${GOLD_DARK}; text-transform: uppercase; letter-spacing: 0.08em; font-size: 12px; padding: 10px 18px; border-radius: 4px; display: inline-flex; align-items: center; gap: 8px; font-family: ${SANS}; text-decoration: none; }
.stew-widget-root .sw-chip { background: ${PAPER}; border: 1px solid ${HAIRLINE}; color: ${INK}; font-family: ${SANS}; font-size: 13px; line-height: 1.3; padding: 8px 14px; border-radius: 999px; cursor: pointer; }
.stew-widget-root .sw-chip:hover { background: #F2EDDF; }
.stew-widget-root .sw-composer { border-top: 1px solid ${HAIRLINE}; background: ${PAPER}; padding-bottom: env(safe-area-inset-bottom); }
.stew-widget-root .sw-textarea { font-family: ${SANS}; font-size: 16px; line-height: 1.4; background: ${PAPER}; color: ${INK}; border: 1px solid ${HAIRLINE}; border-radius: 6px; padding: 12px 14px; width: 100%; resize: none; min-height: 44px; max-height: 140px; }
.stew-widget-root .sw-textarea::placeholder { color: ${MUTED}; }
.stew-widget-root .sw-send { background: ${INK}; color: ${PAPER}; border: none; width: 44px; height: 44px; border-radius: 6px; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; flex: 0 0 auto; }
.stew-widget-root .sw-send:disabled { opacity: 0.35; cursor: not-allowed; }
.stew-widget-root .sw-disclosure { font-size: 11px; color: ${MUTED}; font-family: ${SANS}; margin-top: 10px; line-height: 1.5; }

.stew-widget-root :focus-visible { outline: 2px solid ${INK}; outline-offset: 2px; border-radius: 4px; }
.stew-widget-root .sw-fb-btn { background: transparent; border: none; padding: 4px; color: ${MUTED}; cursor: pointer; display: inline-flex; align-items: center; }
.stew-widget-root .sw-fb-btn:hover { color: ${INK}; }
.stew-widget-root .sw-fb-row { display: inline-flex; gap: 4px; opacity: 0.5; margin-top: 4px; }
.stew-widget-root .sw-fb-row:hover, .stew-widget-root .sw-fb-row:focus-within { opacity: 1; }
.stew-widget-root .sw-dots { display: inline-flex; gap: 6px; align-items: center; padding: 4px 0; }
.stew-widget-root .sw-dot { width: 6px; height: 6px; border-radius: 50%; background: ${INK}; opacity: 0.35; }
.stew-widget-root .sw-fullpane { min-height: 100vh; display: flex; align-items: center; justify-content: center; background: ${PAPER}; padding: 32px; }
.stew-widget-root .sw-fullpane p { font-family: ${SERIF}; font-size: 18px; line-height: 1.6; color: ${INK}; max-width: 32rem; text-align: center; }
.stew-widget-root .sw-visually-hidden { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0; }
@media (prefers-reduced-motion: no-preference) {
  .stew-widget-root .sw-fade-in { animation: sw-fade 200ms ease-out both; }
  .stew-widget-root .sw-dot { animation: sw-pulse 1.2s ease-in-out infinite; }
  .stew-widget-root .sw-dot:nth-child(2) { animation-delay: 0.15s; }
  .stew-widget-root .sw-dot:nth-child(3) { animation-delay: 0.3s; }
}
@keyframes sw-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes sw-pulse { 0%, 100% { opacity: 0.25; } 50% { opacity: 0.9; } }
`;

export default function StewWidget() {
  const [params] = useSearchParams();
  const contactId = (params.get("contact_id") || "").trim();
  const email = (params.get("email") || "").trim();

  const [gate, setGate] = useState<Gate>({ kind: "loading" });
  const [messages, setMessages] = useState<Msg[]>([buildIntro(null)]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const hasUserSent = messages.some((m) => m.role === "user");

  useEffect(() => {
    if (!contactId || !email) {
      setGate({ kind: "missing" });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await post("/public-stew-session", { contact_id: contactId, email });
        if (cancelled) return;
        if (res.status === 403) return setGate({ kind: "forbidden" });
        if (res.status === 503) return setGate({ kind: "unavailable" });
        if (!res.ok) return setGate({ kind: "unavailable" });
        const j = await res.json().catch(() => null);
        const token = j?.session_token;
        if (!token) return setGate({ kind: "unavailable" });
        const firstName =
          typeof j?.first_name === "string" && j.first_name.trim().length > 0
            ? j.first_name.trim()
            : null;
        setMessages([buildIntro(firstName)]);
        setGate({ kind: "ready", token });
        setTimeout(() => inputRef.current?.focus(), 60);
      } catch {
        if (!cancelled) setGate({ kind: "unavailable" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [contactId, email]);

  useEffect(() => {
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: prefersReduced ? "auto" : "smooth",
    });
  }, [messages, sending]);

  const canSend = gate.kind === "ready" && !sending && input.trim().length > 0;

  async function sendText(text: string) {
    if (gate.kind !== "ready" || sending) return;
    const question = text.trim();
    if (!question) return;
    setInput("");
    // Clear any prior failed markers so only the newest failure shows a chip.
    setMessages((m) =>
      m.map((x) =>
        x.role === "assistant" && x.failed
          ? { ...x, failed: false, retryText: undefined }
          : x,
      ),
    );
    setMessages((m) => [...m, { role: "user", content: question }]);
    setSending(true);
    try {
      const res = await post("/public-stew-chat", {
        session_token: gate.token,
        message: question,
      });
      const j = await res.json().catch(() => ({}));
      if (res.status === 429) {
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content: String(
              j?.error || "You are moving quickly. Let's slow down a moment; try again shortly.",
            ),
            cta: "none",
          },
        ]);
      } else if (!res.ok) {
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content: "That one did not go through. Give it a moment and tap try again.",
            cta: "none",
            failed: true,
            retryText: question,
          },
        ]);
      } else {
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content: String(j?.answer || ""),
            ground: (j?.ground as Ground) ?? null,
            section_title: j?.section_title ?? null,
            cta: (j?.cta as Cta) ?? "none",
            cta_url: j?.cta_url ?? null,
          },
        ]);
      }
    } catch {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: "That one did not go through. Give it a moment and tap try again.",
          cta: "none",
          failed: true,
          retryText: question,
        },
      ]);
    } finally {
      setSending(false);
      setTimeout(() => inputRef.current?.focus(), 20);
    }
  }

  if (gate.kind === "loading") {
    return (
      <div className="stew-widget-root">
        <style>{SCOPED_CSS}</style>
        <div className="sw-fullpane">
          <p aria-live="polite">Opening your briefing assistant.</p>
        </div>
      </div>
    );
  }

  if (gate.kind === "forbidden") {
    return (
      <FullPane>
        This assistant opens through your personal briefing link. Use the link from your email, or
        reach out and we will send it again.
      </FullPane>
    );
  }
  if (gate.kind === "missing" || gate.kind === "unavailable") {
    return <FullPane>This assistant opens soon.</FullPane>;
  }

  return (
    <div className="stew-widget-root min-h-screen w-full flex flex-col">
      <style>{SCOPED_CSS}</style>
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-5 pt-6 pb-5"
        aria-live="polite"
      >
        <div className="mx-auto max-w-2xl flex flex-col gap-6">
          {messages.map((m, i) => (
            <div key={i} className="flex flex-col gap-2">
              <MessageRow
                msg={m}
                priorUser={
                  m.role === "assistant" && i > 0 && messages[i - 1]?.role === "user"
                    ? (messages[i - 1] as UserMsg).content
                    : null
                }
                sessionToken={gate.token}
              />
              {m.role === "assistant" && m.failed && m.retryText && !sending && (
                <div>
                  <button
                    type="button"
                    className="sw-chip"
                    onClick={() => void sendText(m.retryText!)}
                  >
                    Try again
                  </button>
                </div>
              )}
            </div>
          ))}
          {!hasUserSent && messages.length === 1 && (
            <div className="flex flex-wrap gap-2 pt-2">
              {STARTER_CHIPS.map((q) => (
                <button
                  key={q}
                  type="button"
                  className="sw-chip"
                  onClick={() => void sendText(q)}
                >
                  {q}
                </button>
              ))}
            </div>
          )}
          {sending && (
            <div className="sw-dots" role="status">
              <span className="sw-dot" />
              <span className="sw-dot" />
              <span className="sw-dot" />
              <span className="sw-visually-hidden">Stew is writing</span>
            </div>
          )}
        </div>
      </div>
      <div className="sw-composer">
        <div className="mx-auto max-w-2xl px-5 py-4">
          <div className="flex items-end gap-2">

            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void sendText(input);
                }
              }}
              placeholder="Ask about the briefing"
              rows={1}
              className="sw-textarea"
            />
            <button
              type="button"
              onClick={() => void sendText(input)}
              disabled={!canSend}
              aria-label="Send"
              className="sw-send"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
          <p className="sw-disclosure">{DISCLOSURE}</p>
        </div>
      </div>
    </div>
  );
}

function FullPane({ children }: { children: React.ReactNode }) {
  return (
    <div className="stew-widget-root">
      <style>{SCOPED_CSS}</style>
      <div className="sw-fullpane">
        <p>{children}</p>
      </div>
    </div>
  );
}

// Display layer only: turn bare URLs in assistant prose into clickable links.
const URL_RE = /(https?:\/\/[^\s<>()]+[^\s<>().,;:!?'"])/g;
function linkify(text: string): React.ReactNode[] {
  const parts = String(text || "").split(URL_RE);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: GOLD_DARK, textDecoration: "underline" }}
      >
        {part}
      </a>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}


function MessageRow({
  msg,
  priorUser,
  sessionToken,
}: {
  msg: Msg;
  priorUser: string | null;
  sessionToken: string;
}) {
  if (msg.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="sw-user-bubble sw-fade-in">{msg.content}</div>
      </div>
    );
  }
  const groundLabel = msg.ground ? GROUND_LABEL[msg.ground] : null;
  const hasCitation = !!(groundLabel || msg.section_title);
  return (
    <div className="flex flex-col sw-fade-in">
      <div className="sw-prose">{linkify(stripDocCodes(msg.content))}</div>
      {hasCitation && (
        <>
          <div className="sw-citation-rule" aria-hidden="true" />
          <div className="sw-citation-text">
            {groundLabel}
            {groundLabel && msg.section_title ? " · " : ""}
            {msg.section_title || ""}
          </div>
        </>
      )}
      {msg.cta && msg.cta !== "none" && msg.cta_url && (
        <div className="pt-3">
          <a href={msg.cta_url} target="_blank" rel="noopener noreferrer" className="sw-cta">
            {CTA_LABEL[msg.cta]}
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </a>
        </div>
      )}
      {priorUser && msg.content && (
        <PublicFeedback sessionToken={sessionToken} question={priorUser} answer={msg.content} />
      )}
    </div>
  );
}

function PublicFeedback({
  sessionToken,
  question,
  answer,
}: {
  sessionToken: string;
  question: string;
  answer: string;
}) {
  type S = "idle" | "prompting" | "submitting" | "done";
  const [state, setState] = useState<S>("idle");
  const [note, setNote] = useState("");

  async function submit(rating: "up" | "down", comment?: string) {
    setState("submitting");
    try {
      await post("/public-stew-feedback", {
        session_token: sessionToken,
        question,
        answer,
        rating,
        comment: comment?.trim() || null,
      });
    } catch {
      /* swallow */
    }
    setState("done");
  }

  if (state === "done") {
    return (
      <div
        className="sw-citation-text"
        style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 8 }}
      >
        <Check className="h-3 w-3" aria-hidden="true" /> Thanks
      </div>
    );
  }
  if (state === "prompting") {
    return (
      <div className="pt-2" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="What was off? Optional."
          rows={2}
          className="sw-textarea"
        />
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            onClick={() => void submit("down", note)}
            className="sw-chip"
          >
            Submit
          </button>
          <button
            type="button"
            onClick={() => void submit("down")}
            className="sw-chip"
            style={{ color: MUTED }}
          >
            Skip
          </button>
        </div>
      </div>
    );
  }
  const busy = state === "submitting";
  return (
    <div className="sw-fb-row">
      <button
        type="button"
        onClick={() => void submit("up")}
        disabled={busy}
        aria-label="Helpful"
        className="sw-fb-btn"
      >
        <ThumbsUp className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        onClick={() => setState("prompting")}
        disabled={busy}
        aria-label="Not helpful"
        className="sw-fb-btn"
      >
        <ThumbsDown className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
